var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import SceneManager from '../../../core/SceneManager';
import { createDynamicWorld } from './DynamicWorldTemplate';
/**
 * WorldRegistry is the catalog of world definitions and the gateway to their scenes.
 * It should coordinate registration, initialization, and rebuilds while delegating
 * actual content/physics decisions to the world definitions and orb ownership layers.
 */
export class WorldRegistry {
    constructor() {
        this.worlds = new Map();
        this.dynamicWorlds = new Map();
        this.initializedScenes = new Set();
        const sceneManager = SceneManager.getInstance();
        sceneManager.on('sceneCleared', ({ sceneName, disposed }) => {
            if (disposed) {
                this.initializedScenes.delete(sceneName);
            }
        });
        sceneManager.on('sceneDeleted', ({ sceneName }) => {
            this.initializedScenes.delete(sceneName);
            this.dropDynamicWorldForScene(sceneName);
        });
    }
    registerWorld(world) {
        const slug = world.slug;
        if (!slug)
            return;
        console.log('[WorldRegistry] Registering world', { slug, scene: world.sceneName, label: world.label });
        if (this.dynamicWorlds.has(slug)) {
            this.dynamicWorlds.delete(slug);
        }
        this.worlds.set(slug, world);
        this.initializedScenes.delete(world.sceneName);
    }
    hasWorld(slug) {
        const normalized = slug.trim().toLowerCase();
        if (!normalized)
            return false;
        return this.worlds.has(normalized) || this.dynamicWorlds.has(normalized);
    }
    getWorld(slug) {
        var _a;
        const normalized = slug.trim().toLowerCase();
        if (!normalized)
            return undefined;
        return (_a = this.worlds.get(normalized)) !== null && _a !== void 0 ? _a : this.dynamicWorlds.get(normalized);
    }
    listWorlds(options = {}) {
        const includeDynamic = options.includeDynamic !== false;
        const staticWorlds = Array.from(this.worlds.values());
        if (!includeDynamic)
            return staticWorlds;
        return [...staticWorlds, ...this.dynamicWorlds.values()];
    }
    getWorldBySceneName(sceneName) {
        const normalized = sceneName.trim().toLowerCase();
        if (!normalized)
            return undefined;
        for (const world of this.worlds.values()) {
            if (world.sceneName.toLowerCase() === normalized) {
                return world;
            }
        }
        for (const world of this.dynamicWorlds.values()) {
            if (world.sceneName.toLowerCase() === normalized) {
                return world;
            }
        }
        return undefined;
    }
    ensureScene(slug, options = {}) {
        console.log('[WorldRegistry] Ensuring scene for world', { slug, forceReload: !!options.forceReload });
        const world = this.getWorld(slug);
        if (!world)
            return null;
        const sceneManager = SceneManager.getInstance();
        const sceneName = world.sceneName;
        if (options.forceReload && sceneManager.hasScene(sceneName)) {
            sceneManager.deleteScene(sceneName, { dispose: true });
            this.initializedScenes.delete(sceneName);
        }
        const scene = sceneManager.hasScene(sceneName)
            ? sceneManager.getScene(sceneName)
            : sceneManager.createScene(sceneName);
        if (!this.initializedScenes.has(sceneName)) {
            console.log('[WorldRegistry] Building scene contents', { sceneName });
            sceneManager.clearScene(sceneName, { dispose: true });
            world.build(scene);
            this.initializedScenes.add(sceneName);
            console.log('[WorldRegistry] Scene ready', { sceneName });
        }
        return scene;
    }
    reloadWorld(slug) {
        return this.ensureScene(slug, { forceReload: true });
    }
    ensureDynamicWorld(slug) {
        if (!slug) {
            console.warn('[WorldRegistry] Refusing to ensure dynamic world without slug');
            return null;
        }
        const normalized = slug.trim().toLowerCase();
        if (!normalized)
            return null;
        console.log('[WorldRegistry] Ensuring dynamic world', { slug: normalized });
        if (this.worlds.has(normalized)) {
            return this.worlds.get(normalized);
        }
        let world = this.dynamicWorlds.get(normalized);
        if (!world) {
            world = createDynamicWorld(normalized);
            this.dynamicWorlds.set(normalized, world);
            this.initializedScenes.delete(world.sceneName);
            console.log('[WorldRegistry] Created new dynamic world', { slug: world.slug, scene: world.sceneName });
            void this.notifyWorldCreated(world);
        }
        return world;
    }
    dropDynamicWorldForScene(sceneName) {
        let removed = false;
        for (const [slug, world] of this.dynamicWorlds.entries()) {
            if (world.sceneName === sceneName) {
                this.dynamicWorlds.delete(slug);
                removed = true;
            }
        }
        if (removed) {
            console.log('[WorldRegistry] Dropped dynamic world for disposed scene', { sceneName });
        }
    }
    notifyWorldCreated(world) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof fetch !== 'function')
                return;
            try {
                yield fetch('/api/worlds', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ slug: world.slug, label: world.label }),
                    keepalive: true,
                });
            }
            catch (error) {
                console.warn('[WorldRegistry] Failed to notify backend about world creation', error);
            }
        });
    }
}
